import React, { useState } from 'react'

import toast from 'react-hot-toast'
import { PiFileAudio } from 'react-icons/pi'

import FileComponent from './FileComponent'
import FilesToUploadList from './FilesToUploadList'
import {
  FilesList,
  FormBtnsContainer,
  InputFileForm,
  UploadedItems,
} from './style'
import UploadedFilesList from './UploadedFilesList'
import { Button, DeleteLogModal } from '../..'
import { useStorage } from '../../../context'
import { AudioRef, FileItem } from '../../../types'
import { SectionSubtitle } from '../../texts'

interface InputFileProps {
  userId?: string
  logUID?: string
  updateLogData: (files: AudioRef[] | undefined) => void
  readOnly: boolean
  defaultValue?: AudioRef[] // 👈 siempre array
}

const InputFile = ({
  userId,
  logUID,
  updateLogData,
  readOnly,
  defaultValue = [],
}: InputFileProps) => {
  const { uploadAudio, deleteAudio, loadingStorage } = useStorage()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [files, setFiles] = useState<FileItem[]>([])

  // Subir un archivo individual desde el array 'files'
  const uploadSingleFile = async (fileItem: {
    id: string
    file: File
    progress: number
  }) => {
    if (!userId || !logUID) return

    const updateProgress = (id: string, p: number) => {
      setFiles((prev) =>
        prev.map((it) => (it.id === id ? { ...it, progress: p } : it)),
      )
    }

    try {
      // Opción: si tu uploadAudio soporta callback de progreso, pasala como último arg
      const res = await uploadAudio(
        fileItem.file,
        userId,
        logUID,
        fileItem.id, // sufijo único opcional
        (percent: number) => updateProgress(fileItem.id, percent),
      )

      updateProgress(fileItem.id, 100)

      setFiles((prev) =>
        prev.map((it) =>
          it.id === fileItem.id
            ? { ...it, uploaded: true, url: res.url ?? res, progress: 100 }
            : it,
        ),
      )

      const newAudio: AudioRef = {
        title: fileItem.file.name,
        url: res?.url ?? res,
        path: res?.fullPath,
      }

      // SIEMPRE array: agregamos al defaultValue que viene del padre ...defaultValue,
      const next: AudioRef[] = [...defaultValue, newAudio]
      updateLogData(next)

      toast.success(`Archivo ${fileItem.file.name} cargado`)
    } catch (error) {
      console.error('Error al subir el archivo:', error)
      toast.error(`Error al subir ${fileItem.file.name}`)
    }
  }

  // Subir todos en paralelo y mostrar progreso individual
  const handleUploadAll = async () => {
    if (!userId || !logUID || files.length === 0) return
    try {
      const pending = files.filter((f) => !f.uploaded) // 👈 solo los no subidos
      if (pending.length === 0) return

      await Promise.all(pending.map((f) => uploadSingleFile(f)))
    } catch (error) {
      console.error('Error en subida paralela:', error)
    }
  }

  const handleDeleteAll = async () => {
    if (!userId || !logUID) return
    setShowConfirmation(false)
    try {
      if (files.length > 0) {
        // primero borramos en storage los que tengan url (archivos recién seleccionados)
        await Promise.all(
          files
            .filter((f) => f.url)
            .map((f) => deleteAudio(userId, logUID, f.url as string)),
        )

        setFiles([])
        // dejamos que el padre decida, pero aquí estamos borrando solo los nuevos
        updateLogData(undefined)
      } else if (defaultValue.length > 0) {
        // NO hay archivos en 'files' -> borrar TODOS los audios persistidos
        await Promise.all(
          defaultValue.map((audio) =>
            deleteAudio(userId, logUID, audio.path ?? audio.url),
          ),
        )
        // avisar al padre que ya no queda ningún audio
        updateLogData(undefined)
      }

      toast.success('Se han eliminado todos los archivos')
    } catch (err) {
      console.error('Error al eliminar todos los archivos:', err)
      toast.error('Error al eliminar algunos archivos')
    }
  }

  return (
    <div>
      {showConfirmation && (
        <DeleteLogModal
          handleClose={() => setShowConfirmation(false)}
          handleDelete={handleDeleteAll}
          message="¿Estas seguro@ que quieres eliminar todos los archivos?"
          hasButton
        />
      )}
      {readOnly ? (
        <>
          {defaultValue.length > 0 ? (
            <FilesList style={{ flexWrap: 'wrap' }}>
              {defaultValue.map((file) => (
                <UploadedItems
                  key={file.url}
                  style={{
                    maxWidth: 'calc(50% - 8px)',
                    height: '-webkit-fill-available',
                  }}
                >
                  <span>
                    <PiFileAudio className="icon" />
                    <a href={file.url} target="_blank" rel="noreferrer">
                      {file.title.slice(0, file.title.lastIndexOf('.'))}
                    </a>
                  </span>
                </UploadedItems>
              ))}
            </FilesList>
          ) : (
            <p>No se ha cargado ningún audio</p>
          )}
        </>
      ) : (
        <InputFileForm>
          <FileComponent
            files={files}
            setFiles={setFiles}
            defaultValue={defaultValue}
          />
          <UploadedFilesList
            userId={userId}
            logUID={logUID}
            updateLogData={updateLogData}
            defaultValue={defaultValue}
            files={files}
          />
          <FilesToUploadList
            userId={userId}
            logUID={logUID}
            defaultValue={defaultValue}
            files={files}
            setFiles={setFiles}
            updateLogData={updateLogData}
            uploadSingleFile={uploadSingleFile}
          />

          <FormBtnsContainer>
            <Button
              onClick={handleUploadAll}
              disabled={
                loadingStorage ||
                files.length === 0 ||
                defaultValue.length > files.length
              }
              label="Subir todo"
              className="light-blue"
            />
            <Button
              onClick={() => setShowConfirmation(true)}
              disabled={
                loadingStorage ||
                (defaultValue.length === 0 && files.length === 0)
              }
              style={{ marginLeft: 8 }}
              label="Eliminar todo"
              className="delete"
            />
          </FormBtnsContainer>
          <SectionSubtitle
            fontSize={11}
            fontWeight={400}
            children="Los audios se guardaran / eliminaran independientemente de la bitacora."
            textAlign="left"
          />
          <SectionSubtitle
            fontSize={11}
            fontWeight={400}
            children="Recuerda eliminar todos los audios si decides no guardar la bitacora."
            textAlign="left"
          />
        </InputFileForm>
      )}
    </div>
  )
}

export default InputFile

// interface InputFileProps {
//   userId?: string
//   logUID?: string
//   updateLogData: (
//     fileReference: { title: string; url: string; path?: string }[] | undefined,
//   ) => void
//   readOnly: boolean
//   // defaultValue?: { title: string; url: string }
//   defaultValue?: { title: string; url: string }[] | undefined
// }

// const handleDeleteAudio = async () => {
//   if (!userId || !logUID) return

//   try {
//     const deleteMessage = await deleteAudio(userId, logUID)

//     setUploadedFile(deleteMessage)
//     updateLogData(undefined)

//     setProgessBar(0)

//     toast.success('Audio eliminado correctamente')
//   } catch (error) {
//     console.error('Error al borrar el archivo:', error)
//     toast.error('Error al eliminar el archivo')
//   } finally {
//     setProgessBar(0)
//   }
// }

// Eliminar todos los archivos (los subidos en la lista)
// const handleDeleteAll = async () => {
//   if (!userId || !logUID) return
//   try {
//     // primero borramos en storage los que tengan url
//     await Promise.all(
//       files
//         .filter((f) => f.url)
//         .map((f) => deleteAudio(userId, logUID, f.url as string)),
//     )

//     setFiles([])
//     updateLogData(undefined)
//     // setUploadedFile({ state: false, message: 'No hay archivo' })
//     toast.success('Todos los archivos eliminados')
//   } catch (err) {
//     console.error('Error al eliminar todos los archivos:', err)
//     toast.error('Error al eliminar algunos archivos')
//   }
// }

// const [uploadedFile, setUploadedFile] = useState<{
//   state: boolean
//   message: string
// }>({
//   state: defaultValue.length > 0,
//   message:
//     defaultValue.length > 0
//       ? `Archivos: ${defaultValue.map((f) => f.title).join(', ')}`
//       : 'No hay archivo',
// })

// const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
//   const selectedFile = event.target.files ? event.target.files[0] : null
//   if (selectedFile) {
//     setProgessBar(0)
//     setFile(selectedFile)
//     setUploadedFile({
//       state: false,
//       message: `Archivo seleccionado: ${selectedFile.name}`,
//     })
//   }
// }
